import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Group,
  HoverCard,
  Image,
  Modal,
  ScrollArea,
  Select,
  Stack,
  Text,
  TextInput,
  Tooltip,
  UnstyledButton,
  VisuallyHidden,
} from '@mantine/core'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { IconAlertTriangle, IconPaperclip, IconPhoto, IconSearch, IconStar, IconX } from '@tabler/icons-react'
import { useMutation } from '@tanstack/react-query'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'

import { confirmLiveModuleAction } from '@/components/confirmLiveModuleAction'
import { LiveModuleBanner } from '@/components/LiveModuleBanner'
import { Table, TableCellValue } from '@/components/Table'
import { theme } from '@/configs/theme'
import { useGlobalState } from '@/hooks/useGlobalState'
import { usePresentationStyles } from '@/hooks/usePresentationStyles'
import type { StepWithValidation } from '@/hooks/useStepsWithValidation'
import { AddMultipleFilesButton } from '@/screens/BulkContentUpload/AddMultipleFilesButton'
import { BulkUploadLauncher, type BulkUploadLauncherHandle } from '@/screens/BulkContentUpload/BulkUploadLauncher'
import { AnalyzingBadge } from '@/screens/ContentModuleFormScreen/AnalyzingBadge'
import type { CMFormSection } from '@/screens/ContentModuleFormScreen/ContentModuleFormScreen'
import { ReviewStepModal } from '@/screens/ContentModuleFormScreen/ReviewStepModal'
import { UploadStepModal } from '@/screens/ContentModuleFormScreen/UploadStepModal'
import type { GetStepsResponse } from '@/services/api/api.types'
import * as pickerClasses from '@/styles/ChangePrimaryPicker.css'
import * as commonClasses from '@/styles/componentCommons.css'
import {
  CONTENT_ACCESS_LABELS,
  contentAccessFlags,
  contentAccessOptions,
  type ContentAccessType,
  resolveContentAccess,
  resolvePrimaryAccess,
} from '@/utils/contentAccess'
import { castStepToZodForm, getProcessErrorMessage, splitMediaSteps } from '@/utils/contentModuleForm'
import { formatContentType } from '@/utils/contentTypes'
import {
  canSelectMediaMode,
  defaultDeliveryModeName,
  deliveryModeLabel,
  demotionForPrimary,
  isMediaModeName,
  storedDeliveryMode,
} from '@/utils/deliveryModes'
import {
  canDeleteStep,
  getStepStatus,
  hasStepFailed,
  primaryFailureRepair,
  type StepStatus,
} from '@/utils/stepReadiness'
import type { TableColumnDef } from '@/utils/table'

export type StepsSectionMode = 'primary' | 'supplementary'

type StepsSectionProps = CMFormSection & {
  /** 'primary' (the designated first step) | 'supplementary' (the rest). */
  mode: StepsSectionMode
}

export const StepsSection = observer(function StepsSection(props: StepsSectionProps) {
  const isPrimaryMode = props.mode === 'primary'
  const isSupplementaryMode = !isPrimaryMode

  // Step ids in optimistic drag order, set only while reorders are in flight.
  const [optimisticOrder, setOptimisticOrder] = useState<string[] | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const bulkUpload = useRef<BulkUploadLauncherHandle>(null)
  const pendingReordersRef = useRef(0)
  const prevStepCountRef = useRef(0)
  const tableWrapperRef = useRef<HTMLDivElement>(null)
  const { api, bulkUploadStore } = useGlobalState()

  // Needed here to resolve the mode a swap has to fall back to.
  const stylesQuery = usePresentationStyles()

  // Reordering overrides order, not content: reconciling ids against live query
  // data keeps background adds, deletes, and status changes visible while a
  // reorder settles, and means a request that never settles can't freeze the table.
  const steps = useMemo(() => {
    const serverSteps = props.stepsQuery.data
    if (!optimisticOrder) return serverSteps

    const byId = new Map(serverSteps.map(step => [step.stepId, step]))
    const reordered = optimisticOrder.flatMap(stepId => byId.get(stepId) ?? [])
    const alreadyPlaced = new Set(optimisticOrder)

    return [...reordered, ...serverSteps.filter(step => !alreadyPlaced.has(step.stepId))]
  }, [optimisticOrder, props.stepsQuery.data])

  const { primary: primaryStep, supplementary: supplementarySteps } = useMemo(
    () => splitMediaSteps(steps, props.cmQuery.data?.primaryStepId),
    [steps, props.cmQuery.data?.primaryStepId],
  )

  const displaySteps = useMemo<StepWithValidation[]>(
    () => (isPrimaryMode ? (primaryStep ? [primaryStep] : []) : supplementarySteps),
    [primaryStep, supplementarySteps, isPrimaryMode],
  )

  const hasPrimary = !!primaryStep
  const hasDisplayed = displaySteps.length > 0
  // Deleting a failed primary only empties the slot when it is the module's only file.
  // With siblings the backend promotes one, so decision 7 sends the expert to Change.
  const primaryIsOnlyFile = supplementarySteps.length === 0

  const scrollToBottom = useCallback(() => {
    const el = tableWrapperRef.current?.querySelector('.steps-scroll-container') as HTMLElement | null
    if (el) {
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight
      })
    }
  }, [])

  useEffect(() => {
    if (props.stepSavedTrigger && props.stepSavedTrigger > 0) {
      scrollToBottom()
    }
  }, [props.stepSavedTrigger, scrollToBottom])

  useEffect(() => {
    const count = displaySteps.length
    if (count > 0 && prevStepCountRef.current === 0) {
      scrollToBottom()
    }
    prevStepCountRef.current = count
  }, [displaySteps.length, scrollToBottom])

  const releaseReorder = useCallback(() => {
    pendingReordersRef.current--
    // Clamped rather than `=== 0`: an unbalanced release must not strand the
    // counter above zero and pin the optimistic order for good.
    if (pendingReordersRef.current <= 0) {
      pendingReordersRef.current = 0
      setOptimisticOrder(null)
    }
  }, [])

  const reorderStepsMutation = useMutation({
    mutationFn: api.reorderSteps,
    onSuccess: () => {
      props.stepsQuery.refetch().finally(releaseReorder)
    },
    onError: error => {
      console.error('Failed to reorder steps:', error)
      // The rows visibly snap back, so without this the expert sees their drag undone
      // with no reason given — the sibling row actions all notify.
      notifications.show({
        title: 'Could Not Reorder Files',
        message: 'There was an issue saving the new order. Please try again.',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
      releaseReorder()
    },
  })

  // Per-row Content Access, decision 3. Two writes, same pair the file modal makes:
  // `show_in_app` on the source resource and `use_in_rag` on the step. Only the changed
  // field is sent — /cm/update_resource leaves anything omitted alone.
  const [savingAccessFor, setSavingAccessFor] = useState<string | null>(null)
  // The Select is controlled from step data, so on its own it keeps showing the old access
  // for the whole round trip and the choice reads as ignored. Held until the refreshed step
  // agrees, rather than until the write resolves: `stepUpdateMutation.onSuccess` refreshes
  // the row without being awaited, so dropping it any earlier flicks the old value back.
  const [pendingAccess, setPendingAccess] = useState<{ stepId: string; access: ContentAccessType } | null>(null)

  useEffect(() => {
    if (!pendingAccess) return

    const step = steps.find(candidate => candidate.stepId === pendingAccess.stepId)
    if (!step) {
      setPendingAccess(null)
      return
    }

    const current = resolveContentAccess({
      type: step.contentType,
      showInAppSupported: step.showInAppSupported,
      showInApp: step.showInApp,
      useInRag: step.useInRag,
    })
    if (current === pendingAccess.access) setPendingAccess(null)
  }, [steps, pendingAccess])

  // The swap outlives `cmUpdateMutation.isPending` by two writes and two refetches, and
  // the button is what stops a second one being started on top of the first.
  const [swapping, setSwapping] = useState(false)
  const [changePrimaryOpen, setChangePrimaryOpen] = useState(false)

  // The stable members only: `props.stepsQuery` is a memo whose identity moves with step
  // data, so depending on it rebuilt the `columns` memo on every status poll and
  // re-rendered the whole supplementary table with it.
  const { mutateAsync: updateResource } = props.resourceUpdateMutation
  const { mutateAsync: updateStep } = props.stepUpdateMutation
  const { refetch: refetchSteps } = props.stepsQuery
  const changeContentAccess = useCallback(
    async (step: StepWithValidation, access: ContentAccessType) => {
      if (!step.sourceResourceId) return

      // Same gate as every other file-level action: this writes straight through, so on a
      // published module it hides the file, or drops it out of RAG, the moment it lands.
      if (props.isLive) {
        const ok = await confirmLiveModuleAction({
          body: 'Changing content access will immediately update what members see. Do you want to continue?',
          confirmLabel: 'Change and Publish',
        })
        if (!ok) return
      }

      const { showInApp, useInRag } = contentAccessFlags(access)

      setSavingAccessFor(step.stepId)
      setPendingAccess({ stepId: step.stepId, access })
      try {
        await updateResource({
          resource_id: step.sourceResourceId,
          step_id: step.stepId,
          show_in_app: showInApp,
        })
        await updateStep({ step_id: step.stepId, use_in_rag: useInRag })
      } catch (error) {
        console.error('Failed to change content access:', error)
        notifications.show({
          title: 'Could Not Change Content Access',
          message: 'There was an issue saving this change. Please try again.',
          color: 'red',
          classNames: commonClasses.notificationFilled,
          position: 'bottom-left',
        })
        // The optimistic value is a promise the write did not keep, and two writes mean a
        // failure between them leaves the row half-changed. Only this path needs the
        // refresh: `stepUpdateMutation.onSuccess` already refreshes the step it wrote, and
        // doing it here as well sent the list fetch and the per-step pair twice for one
        // selection, the second cancelling the first.
        setPendingAccess(null)
        await refetchSteps({ stepId: step.stepId })
      } finally {
        setSavingAccessFor(null)
      }
    },
    [updateResource, updateStep, refetchSteps, props.isLive],
  )

  const columns = useMemo(() => {
    if (isPrimaryMode) return stepColDefs

    const accessColumn: TableColumnDef<StepWithValidation> = {
      id: 'contentAccess',
      header: 'Content Access',
      enableGlobalFilter: false,
      enableSorting: false,
      size: 170,
      meta: { grow: false },
      cell: ({ row }) => (
        <ContentAccessCell
          step={row.original}
          onChange={changeContentAccess}
          disabled={savingAccessFor === row.original.stepId || row.original.inProcess === true}
          pending={pendingAccess?.stepId === row.original.stepId ? pendingAccess.access : undefined}
        />
      ),
    }

    const statusIndex = stepColDefs.findIndex(column => column.id === 'status')
    return [...stepColDefs.slice(0, statusIndex), accessColumn, ...stepColDefs.slice(statusIndex)]
  }, [isPrimaryMode, changeContentAccess, savingAccessFor, pendingAccess])

  // Decision 4, on C2-2596: the primary is designated rather than inferred from order.
  // Only `primary_step_id` is sent — /cm/update writes the fields it is given, so a
  // swap cannot carry unsaved form edits with it.
  const changePrimary = useCallback(
    async (step: StepWithValidation) => {
      if (!props.taskId) return
      setSwapping(true)
      // The swap persists before the access writes, so a later failure must not be
      // reported as the swap not having happened.
      let swapped = false
      try {
        const facts = { type: step.contentType, showInAppSupported: step.showInAppSupported }

        // The mode correction rides the same write as the swap. The swap persists the
        // moment it is confirmed, so a correction left on the form for the expert to save
        // left the module stored as Media with a primary that cannot carry it, and
        // discarding or closing the tab made that permanent.
        // Derived from what is stored, not from the form. The swap persists on its own, so
        // the correction has to make the *stored* module coherent: a form already moved off
        // Media unsaved yields no demotion, and discarding then leaves the module stored as
        // Media with a primary that cannot carry it.
        const fallback = demotionForPrimary(
          storedDeliveryMode(props.cmQuery.data ?? {}),
          facts,
          stylesQuery.data?.map(style => style.name) ?? [],
        )
        const fallbackStyle = fallback ? stylesQuery.data?.find(style => style.name === fallback) : undefined

        // The form gets its own answer. The write above fixes what is *stored*; the form may
        // hold an unsaved mode of the expert's that the new primary can carry perfectly well,
        // and pushing the stored correction into it would discard their choice with no notice.
        // Only a form that is itself on Media needs moving.
        const formFallback = demotionForPrimary(
          storedDeliveryMode({
            presentationStyleName: props.moduleForm.getValues().deliveryStyleName,
            presentationStyle: props.moduleForm.getValues().deliveryStyle,
            isMediaMode: props.cmQuery.data?.isMediaMode,
          }),
          facts,
          stylesQuery.data?.map(style => style.name) ?? [],
        )
        const formFallbackStyle = formFallback
          ? stylesQuery.data?.find(style => style.name === formFallback)
          : undefined

        // `lock: false` because this is a mid-edit write, not the expert leaving. /cm/update
        // clears `locked_by` by default, which would hand the module to someone else while
        // they are still in it.
        await props.cmUpdateMutation.mutateAsync({
          task_id: props.taskId,
          primary_step_id: step.stepId,
          lock: false,
          ...(fallbackStyle
            ? { presentation_style_name: fallbackStyle.name, presentation_style: fallbackStyle.description }
            : {}),
        })
        swapped = true

        if (formFallbackStyle) {
          props.moduleForm.setFieldValue('deliveryStyleName', formFallbackStyle.name)
          props.moduleForm.setFieldValue('deliveryStyle', formFallbackStyle.description)

          // The correction only counts as saved when the write above actually carried it, and
          // it carried `fallbackStyle`, which is derived from the *stored* mode while the form
          // was moved to `formFallbackStyle`, derived from the form's. The two diverge: a module
          // on a legacy custom style whose expert has just picked Media unsaved yields no stored
          // demotion at all, so `/cm/update` sends only the primary. Rebasing then would mark a
          // correction the server never received as clean, and the expert walks away from it
          // with no prompt and nothing cached, finding the old style still there next visit.
          //
          // Scoped to the two fields this wrote, so it needs no before-and-after comparison —
          // the earlier version read the form's clean state *before* the request and reset the
          // whole form after it, which was a round trip out of date and wiped anything typed
          // meanwhile. `getInitialValues` returns the current baseline, so merging over it moves
          // only these two and preserves a rebase an auto-save made earlier.
          if (fallbackStyle && fallbackStyle.name === formFallbackStyle.name) {
            props.moduleForm.resetDirty({
              ...props.moduleForm.getInitialValues(),
              deliveryStyleName: formFallbackStyle.name,
              deliveryStyle: formFallbackStyle.description,
            })
            props.moduleForm.setTouched(current => {
              const next = { ...current }
              delete next.deliveryStyleName
              delete next.deliveryStyle
              return next
            })
          }
        }

        // Decision 2 again, on the way in: the primary's access is resolved rather than
        // asked, and the row that would let anyone correct it by hand is not shown for the
        // primary. Promoting a Media Only file would otherwise strand it outside RAG.
        // Skipped when the status snapshot hasn't landed, since `contentType` is not
        // trustworthy until it has.
        // Resolved from the format, without the stored flag. Decision 27 keeps a deliberate
        // hide against an *incidental* save, because that rewrite was silent; this one is the
        // expert choosing this file to lead, and the picker row says "Content access will
        // change to Full Access" before they confirm. A hidden lead file is a contradiction
        // either way — corrected here because it can be, demoted in the section where it
        // cannot be said out loud.
        const resolved = resolvePrimaryAccess(facts)
        const current = resolveContentAccess({ ...facts, showInApp: step.showInApp, useInRag: step.useInRag })
        if (props.stepsQuery.isStatusReady && step.sourceResourceId && current !== resolved) {
          const { showInApp, useInRag } = contentAccessFlags(resolved)
          await props.resourceUpdateMutation.mutateAsync({
            resource_id: step.sourceResourceId,
            step_id: step.stepId,
            show_in_app: showInApp,
          })
          await props.stepUpdateMutation.mutateAsync({ step_id: step.stepId, use_in_rag: useInRag })
        }
      } catch (error) {
        console.error('Failed to change the primary file:', error)
        notifications.show({
          title: swapped ? 'Content Access Not Updated' : 'Could Not Change Primary File',
          message: swapped
            ? `${step.name || 'This file'} is now your Primary File, but its content access could not be updated, so it may still be held back from the Sidekick.`
            : 'There was an issue saving this change. Please try again.',
          color: 'red',
          classNames: commonClasses.notificationFilled,
          position: 'bottom-left',
        })
      } finally {
        // Dropped before the module refetch hands this step the lead. `useStepResources`
        // keys on the step, so the delivery-mode section switches to this one the moment
        // `primaryStepId` changes — and a cached pre-swap entry is served synchronously as
        // `isSuccess`, so it would read the access this swap has just corrected as still
        // hidden and demote the module off Media for it. Removed rather than invalidated:
        // an invalidated entry still answers from cache on that first render. The
        // mutation's own `onSuccess` cannot cover this, since it invalidates the *staged*
        // step, which during a swap is not the one written.
        api.queryClient.removeQueries({ queryKey: ['step', 'resources', 'all', step.stepId] })

        // Refreshed even when a write failed part-way. The swap goes first, so a later
        // failure still leaves it done — showing the old primary would hide that, and the
        // expert's next move would be made against a module they can't see the state of.
        // `stepId` is what patches the content snapshot, which is where `showInApp` lives.
        await props.cmQuery.refetch()
        await props.stepsQuery.refetch({ stepId: step.stepId })
        setSwapping(false)
      }
    },
    [
      api.queryClient,
      props.taskId,
      props.moduleForm,
      stylesQuery.data,
      props.cmUpdateMutation,
      props.cmQuery,
      props.stepsQuery,
      props.resourceUpdateMutation,
      props.stepUpdateMutation,
    ],
  )

  async function openChangePrimary() {
    // Same gate as the other file-level actions: this writes straight through, so on a
    // published module it changes what members lead with the moment it is confirmed.
    if (props.isLive) {
      const ok = await confirmLiveModuleAction({
        body: 'Changing the primary file will immediately update what members see. Do you want to continue?',
        confirmLabel: 'Change and Publish',
      })
      if (!ok) return
    }

    setChangePrimaryOpen(true)
  }

  // Read where it is rendered rather than captured at open. `modals.open` stores the
  // element it is given, so the candidates handed to it never updated — a file that
  // finished processing while the dialog was up stayed disabled until it was closed and
  // reopened, with nothing saying so. Read the stored mode, not the form: it is what the
  // swap corrects against, so reading the form here would warn about one thing and save
  // another. A module older than C2-2598 has no recorded choice, which is why this goes
  // through `storedDeliveryMode` rather than the name alone (decision 28).
  const isMediaNow = isMediaModeName(storedDeliveryMode(props.cmQuery.data ?? {}))

  const modalStyles = {
    styles: {
      body: {
        paddingTop: theme.rem(16),
        backgroundColor: theme.colors.gray[0],
      },
    },
  }

  function openUploadModal() {
    props.openModal?.({
      Component: UploadStepModal,
      ModalProps: modalStyles,
    })
  }

  function openReviewModal() {
    props.openModal?.({
      Component: ReviewStepModal,
      ModalProps: modalStyles,
    })
  }

  const addNewContent = async () => {
    if (props.isLive) {
      const confirmed = await confirmLiveModuleAction({
        body: 'When you save this new file, it will immediately be available to your End Users in the app. Do you want to continue?',
      })
      if (!confirmed) return
    }
    stageStep()
    openUploadModal()
  }

  const addMultipleFiles = async () => {
    if (props.isLive) {
      const confirmed = await confirmLiveModuleAction({
        body: 'When these files finish uploading, they will immediately be available to your End Users in the app. Do you want to continue?',
      })
      if (!confirmed) return
    }
    bulkUpload.current?.open({ taskId: props.taskId!, moduleTitle: props.moduleForm.getValues().name })
  }

  // Supplementary mode reorders only the subset; prepend the primary so the
  // API receives the full ordered list.
  const handleRowOrderChange = (newData: StepWithValidation[]) => {
    if (!props.taskId) return

    const fullList: StepWithValidation[] = isSupplementaryMode
      ? [...(primaryStep ? [primaryStep] : []), ...newData]
      : newData

    pendingReordersRef.current++
    setOptimisticOrder(fullList.map(step => step.stepId))

    reorderStepsMutation.mutate({
      task_id: props.taskId,
      step_ids: fullList.map(step => step.stepId),
    })
  }

  async function deleteStep(data: GetStepsResponse['0']) {
    modals.openConfirmModal({
      title: props.isLive ? undefined : 'Delete content?',
      withCloseButton: !props.isLive,
      centered: true,
      children: props.isLive ? (
        <Stack gap="md">
          <LiveModuleBanner />
          <Text size="sm">
            Deleting this file will immediately remove it for End Users in the app. This action cannot be undone.
          </Text>
        </Stack>
      ) : (
        <Text size="sm">
          Are you sure you want to delete this content? This action is destructive and you will lose all of the progress
          made for this content.
        </Text>
      ),
      labels: { confirm: 'Delete Content', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      groupProps: { mt: 'xl' },
      onConfirm: () => {
        props.stepForm.setValues(castStepToZodForm())
        props.stepDeleteMutation?.mutate({ step_id: data.stepId, task_id: props.taskId! })
      },
    })
  }

  function stageStep(data?: GetStepsResponse['0']) {
    props.stepForm.setValues(castStepToZodForm(data))
    props.stepForm.resetDirty()
    props.stepForm.resetTouched()
  }

  const emptyStateProps = isPrimaryMode
    ? {
        title: 'No Primary File',
        description: 'Every module leads with one file. Upload it to get started.',
        Icon: IconStar,
      }
    : {
        title: 'No Supplementary Content',
        description: 'Add additional files to enrich this module.',
        Icon: IconPaperclip,
      }

  const isAddPending =
    props.cmContentQuery.isFetching || props.cmCreateMutation.isPending || props.stepCreateMutation.isPending

  // Primary: single-slot, hide once filled. Supplementary: requires a primary first.
  const showAddButton = isPrimaryMode ? !hasPrimary : hasPrimary

  const addButtonLabel = isPrimaryMode
    ? 'Add Primary File'
    : hasDisplayed
      ? 'Add Another File'
      : 'Add Supplementary File'

  return (
    <Stack gap="xxxl">
      <Box ref={tableWrapperRef}>
        {hasDisplayed && !isPrimaryMode && (
          <Group justify="flex-end" mb="xs">
            <TextInput
              size="xs"
              placeholder="Search by file name or type..."
              leftSection={<IconSearch size={14} stroke={1.5} />}
              rightSection={
                searchQuery ? (
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="xs"
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear search">
                    <IconX size={12} aria-hidden="true" />
                  </ActionIcon>
                ) : null
              }
              value={searchQuery}
              onChange={e => setSearchQuery(e.currentTarget.value)}
              w={260}
            />
          </Group>
        )}
        {hasDisplayed && (
          <Table
            data={displaySteps}
            columns={columns}
            getRowId={step => step.stepId}
            rowActionsInclude={['edit', 'delete']}
            enableTopToolbar={false}
            enableGlobalFilter={!isPrimaryMode}
            {...(!isPrimaryMode && { globalFilter: searchQuery, onGlobalFilterChange: setSearchQuery })}
            // `/step/reorder` rewrites the order from this list, which lags the files a bulk upload adds.
            enableRowOrdering={!isPrimaryMode && !searchQuery && !bulkUploadStore.jobHoldsModule(props.taskId ?? '')}
            enableRowVirtualization={true}
            enableStickyHeader={true}
            containerProps={{ style: { maxHeight: '60vh' }, className: 'steps-scroll-container' }}
            rowVirtualizerOverscan={5}
            onRowActionClick={async (action, row) => {
              const hasError = hasStepFailed(row.original)
              if (action === 'delete') {
                if (!canDeleteStep({ isPrimary: isPrimaryMode, hasError, isOnlyFile: primaryIsOnlyFile })) return
                deleteStep(row.original)
              } else if (action === 'edit') {
                const isProcessing = row.original.inProcess === true
                if (isProcessing || hasError) return
                if (props.isLive) {
                  const confirmed = await confirmLiveModuleAction({
                    body: 'Edits to this file will be immediately available to your End Users in the app. Do you want to continue?',
                  })
                  if (!confirmed) return
                }
                stageStep(row.original)
                openReviewModal()
              }
            }}
            rowActionsTooltipLabels={row => {
              const hasError = hasStepFailed(row.original)
              return {
                edit: hasError
                  ? isPrimaryMode
                    ? `This file failed to process. ${primaryFailureRepair(!primaryIsOnlyFile)}`
                    : 'This file failed to process. Delete it and upload again.'
                  : row.original.inProcess
                    ? 'Content is still being analyzed'
                    : 'Review File',
                delete: canDeleteStep({ isPrimary: isPrimaryMode, hasError, isOnlyFile: primaryIsOnlyFile })
                  ? 'Delete File'
                  : // Change only renders once there is something to change to, so a lone
                    // primary cannot be sent there.
                    primaryIsOnlyFile
                    ? 'Every module needs a Primary File, so this one cannot be deleted.'
                    : 'Use Change to swap the primary file.',
              }
            }}
            rowActionsButtonProps={row => {
              const hasError = hasStepFailed(row.original)
              return {
                edit: { disabled: row.original.inProcess === true || hasError },
                delete: {
                  disabled: !canDeleteStep({ isPrimary: isPrimaryMode, hasError, isOnlyFile: primaryIsOnlyFile }),
                },
              }
            }}
            EmptyStateProps={emptyStateProps}
            actionsColumnSize={98}
            onRowOrderChange={handleRowOrderChange}
          />
        )}

        {showAddButton && (
          <Group justify="center" mt="xl">
            <Stack gap="xs" align="center">
              <Button
                loading={isAddPending}
                onClick={() => {
                  addNewContent()
                }}>
                {addButtonLabel}
              </Button>
            </Stack>
            {isSupplementaryMode && <AddMultipleFilesButton disabled={isAddPending} onClick={addMultipleFiles} />}
          </Group>
        )}

        {isPrimaryMode && hasPrimary && supplementarySteps.length > 0 && (
          <Group justify="center" mt="xl">
            <Button variant="light" onClick={openChangePrimary} loading={swapping}>
              Change
            </Button>
          </Group>
        )}

        {isSupplementaryMode && <BulkUploadLauncher ref={bulkUpload} mode="supplementary" />}

        {isSupplementaryMode && !hasPrimary && (
          <Group justify="center" mt="xl">
            <Text size="sm" c="dimmed">
              Add a Primary File first to enable supplementary content.
            </Text>
          </Group>
        )}
      </Box>

      {/* TODO: Re-enable when we have multiple sections */}
      {/* <Grid gap="xxxl">
        <Grid.Col span={12}>
          <Radio.Group label="Module Flow" withAsterisk {...props.moduleForm.getInputProps('presentationMode')}>
            <Group gap="xl" align="stretch">
              <RadioCard
                value={moduleSchema.shape.presentationMode.enum.member_directed}
                label="User Directed"
                description="Users can access the content in any order."
              />

              <RadioCard
                value={moduleSchema.shape.presentationMode.enum.sequential}
                label={<ComingSoon prefix="Sequential " dimPrefix />}
                disabled
                description="Their first time through, the user must go through the content in the order it's laid out."
              />
            </Group>
          </Radio.Group>
        </Grid.Col>
      </Grid> */}

      {/* The current primary is the row above the picker, not an option in it: offering it
          back costs the top slot and does nothing when taken. */}
      <Modal
        opened={changePrimaryOpen}
        onClose={() => setChangePrimaryOpen(false)}
        title="Change primary file"
        centered
        // Wide enough for a row's type and its caveat to sit on one line.
        size="lg">
        <ChangePrimaryBody
          candidates={supplementarySteps}
          isMediaNow={isMediaNow}
          onCancel={() => setChangePrimaryOpen(false)}
          onConfirm={step => {
            setChangePrimaryOpen(false)
            changePrimary(step)
          }}
        />
      </Modal>
    </Stack>
  )
})

const PRIMARY_BLOCKED_NOTES: Partial<Record<StepStatus, string>> = {
  failed: 'Processing failed',
  analyzing: 'Still processing',
  preparing: 'Still preparing',
  transcodeFailed: 'Transcoding failed',
}

/**
 * Whether a file can take over as primary, and what to say about it if it can't.
 *
 * Only a processed file can lead: the outline is built from its content, and until the
 * status snapshot lands `contentType` is not trustworthy enough to resolve the primary's
 * access from. A file that would cost the module its delivery mode stays selectable — the
 * expert is allowed to make that trade, they just have to be told about it first, and only
 * while the trade can actually be carried out.
 */
function primaryEligibility(
  step: StepWithValidation,
  isMediaNow: boolean,
  fallbackMode?: string,
): { selectable: boolean; note?: string; blocked?: boolean } {
  const blockedNote = PRIMARY_BLOCKED_NOTES[getStepStatus(step)]
  if (blockedNote) return { selectable: false, note: blockedNote }
  // Stricter than the badge, deliberately: this resolves the candidate's access, so it
  // waits for the snapshot even where the badge has already moved on to Review Needed.
  if (step.statusPending || !step.sourceResourceId) return { selectable: false, note: 'Still reading its details' }

  // The same rule the demotion runs after the swap, so the dialog cannot promise a mode
  // the module is about to lose.
  if (isMediaNow && !canSelectMediaMode({ type: step.contentType, showInAppSupported: step.showInAppSupported })) {
    // No fallback resolves exactly when `demotionForPrimary` returns nothing — the catalog
    // failed to load, or offers no mode but Media. The swap persists on its own, so letting
    // it through would store the module as Media with a primary that cannot carry it, and
    // the warning promising otherwise is the only reason to accept the trade.
    if (!fallbackMode) {
      return {
        selectable: false,
        note: "Delivery modes couldn't be loaded, so this file can't lead yet. Reload the page to try again.",
      }
    }
    return { selectable: true, note: `Delivery mode will change to ${fallbackMode}`, blocked: true }
  }

  return { selectable: true }
}

/**
 * The table's Content Access column, read-only. Blank when the source resource has not
 * landed: with no stored flags to read, the resolver answers Full Access, which is a claim
 * about a file nothing is known about yet.
 */
function accessLabel(step: StepWithValidation): string {
  if (!step.sourceResourceId) return '\u2014'

  const access = resolveContentAccess({
    type: step.contentType,
    showInAppSupported: step.showInAppSupported,
    showInApp: step.showInApp,
    useInRag: step.useInRag,
  })

  return CONTENT_ACCESS_LABELS[access]
}

/**
 * Named when leading will move the file's Content Access. Decision 2 resolves the primary's
 * access rather than asking, so a Media Only file joins RAG the moment it leads — which
 * re-indexes it and republishes its FAQs. The dialog already says when a swap costs the
 * module its delivery mode; doing this one silently was the odd one out.
 */
function accessChangeNote(step: StepWithValidation): string | undefined {
  if (!step.sourceResourceId) return undefined

  const facts = { type: step.contentType, showInAppSupported: step.showInAppSupported }
  const current = resolveContentAccess({ ...facts, showInApp: step.showInApp, useInRag: step.useInRag })
  // The same format-only resolution the swap writes, so the note and the write agree.
  const resolved = resolvePrimaryAccess(facts)
  if (current === resolved) return undefined

  return `Content access will change to ${CONTENT_ACCESS_LABELS[resolved]}`
}

/** The mockup's swap picker: one row per alternative, laid out as the file table above it. */
function ChangePrimaryBody(props: {
  candidates: StepWithValidation[]
  isMediaNow: boolean
  onCancel: () => void
  onConfirm: (step: StepWithValidation) => void
}) {
  const noteIdBase = useId()
  const [selected, setSelected] = useState<string | null>(null)

  // Only used to name the mode a swap would fall back to, so a miss costs the sentence its
  // target, not its meaning.
  const stylesQuery = usePresentationStyles()

  // The same call the demotion itself makes, against the same facts, so the dialog names
  // the mode the module will actually land on rather than assuming Discovery.
  const fallbackModeFor = useCallback(
    (step: StepWithValidation) => {
      const styles = stylesQuery.data
      if (!styles?.length) return undefined

      const name = defaultDeliveryModeName(
        { type: step.contentType, showInAppSupported: step.showInAppSupported },
        styles.map(style => style.name),
      )
      if (!name) return undefined

      return deliveryModeLabel(name, styles.find(style => style.name === name)?.displayName || name)
    },
    [stylesQuery.data],
  )

  // Resolved once per render and read back through for the selection: the candidates are
  // live now, so a file can fail or finish while the dialog is open. Confirming has to go
  // through the same eligibility the row was disabled by, or a file that failed underneath
  // the expert is still promotable.
  const rows = props.candidates.map(step => ({
    step,
    ...primaryEligibility(step, props.isMediaNow, fallbackModeFor(step)),
  }))
  const picked = rows.find(row => row.selectable && row.step.stepId === selected)?.step

  return (
    <Stack gap="md">
      {props.candidates.length === 0 ? (
        <Text size="sm">This module has no other file to lead with. Add one first.</Text>
      ) : (
        <Stack gap="xs">
          <Text size="sm" fw={500}>
            Which file should lead instead?
          </Text>

          <Box className={pickerClasses.header}>
            <Box component="span" className={pickerClasses.headerSpacer} />
            <Text size="sm" fw={700} className={pickerClasses.name}>
              File Name
            </Text>
            <Text size="sm" fw={700} className={pickerClasses.columnType}>
              File Type
            </Text>
            <Text size="sm" fw={700} className={pickerClasses.columnAccess}>
              Content Access
            </Text>
          </Box>

          {/* Every other file in the module lands here, and the table above it is
              virtualized for exactly that reason. Capped and scrolled rather than
              virtualized: that would mean a direct dependency on `@tanstack/react-virtual`
              for one dialog, where the thumbnails are what actually cost and `loading="lazy"`
              is what stops them being fetched. Mantine's scrollbar is an overlay, so the
              rows keep the header's column widths. */}
          <ScrollArea.Autosize mah="45vh" type="auto">
            <Stack gap="xs" role="radiogroup" aria-label="Files that could lead this module">
              {rows.map(({ step, selectable, note, blocked }) => {
                const label = step.name || step.youtubeTitle || 'Untitled'
                const isSelected = selected === step.stepId

                const notes: { text: string; warn: boolean }[] = []
                if (note) notes.push({ text: note, warn: !!blocked })
                const accessNote = selectable ? accessChangeNote(step) : undefined
                if (accessNote) notes.push({ text: accessNote, warn: false })

                return (
                  <Stack key={step.stepId} gap={4}>
                    <UnstyledButton
                      role="radio"
                      aria-checked={isSelected}
                      aria-describedby={
                        notes.length
                          ? notes.map((_, index) => `${noteIdBase}-${step.stepId}-${index}`).join(' ')
                          : undefined
                      }
                      disabled={!selectable}
                      data-selected={isSelected || undefined}
                      className={pickerClasses.row}
                      onClick={() => setSelected(step.stepId)}>
                      <PickerThumbnail src={step.thumbnailUrl} label={label} />
                      <Text span size="sm" fw={500} c={step.name ? undefined : 'dimmed'} className={pickerClasses.name}>
                        {label}
                      </Text>
                      <Text span size="sm" className={pickerClasses.columnType}>
                        {formatContentType(step.contentType)}
                      </Text>
                      <Text span size="sm" className={pickerClasses.columnAccess}>
                        {accessLabel(step)}
                      </Text>
                    </UnstyledButton>

                    {notes.map((entry, index) => (
                      <Text
                        key={entry.text}
                        id={`${noteIdBase}-${step.stepId}-${index}`}
                        size="sm"
                        c={entry.warn ? 'yellow.8' : 'dimmed'}
                        className={pickerClasses.note}>
                        {entry.warn && <IconAlertTriangle size={16} aria-hidden />}
                        {entry.text}
                      </Text>
                    ))}
                  </Stack>
                )
              })}
            </Stack>
          </ScrollArea.Autosize>
        </Stack>
      )}

      <Group justify="flex-end" gap="sm">
        <Button variant="default" onClick={props.onCancel}>
          Cancel
        </Button>
        <Button disabled={!picked} onClick={() => picked && props.onConfirm(picked)}>
          Make primary
        </Button>
      </Group>
    </Stack>
  )
}

/**
 * The table's thumbnail without its hover preview, in a span: the picker row is a button,
 * and a zoom-on-hover inside a clickable row competes with the click it is offering.
 */
function PickerThumbnail({ src, label }: { src?: string | null; label: string }) {
  return (
    <Box component="span" style={THUMBNAIL_CELL_BOX}>
      {src ? (
        <Image src={src} alt={`Thumbnail for ${label}`} w={56} h={32} fit="cover" loading="lazy" />
      ) : (
        <IconPhoto size={16} stroke={1.5} color="var(--mantine-color-gray-5)" />
      )}
    </Box>
  )
}

const THUMBNAIL_CELL_BOX = {
  width: 56,
  height: 32,
  borderRadius: 6,
  overflow: 'hidden',
  border: '1px solid var(--mantine-color-gray-3)',
  backgroundColor: 'var(--mantine-color-gray-1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
} as const

const THUMBNAIL_CELL_BOX_INTERACTIVE = { ...THUMBNAIL_CELL_BOX, cursor: 'zoom-in' } as const

function ThumbnailCell({ src, label }: { src?: string | null; label?: string | null }) {
  if (!src) {
    return (
      <Box style={THUMBNAIL_CELL_BOX}>
        <IconPhoto size={16} stroke={1.5} color="var(--mantine-color-gray-5)" />
      </Box>
    )
  }

  const alt = label ? `Thumbnail for ${label}` : 'File thumbnail'

  return (
    <HoverCard
      openDelay={150}
      closeDelay={50}
      position="right"
      withArrow
      shadow="md"
      withinPortal
      withRoles={false}
      events={{ touch: true }}>
      <HoverCard.Target>
        <Box style={THUMBNAIL_CELL_BOX_INTERACTIVE}>
          <Image src={src} alt={alt} w={56} h={32} fit="cover" />
        </Box>
      </HoverCard.Target>
      <HoverCard.Dropdown p={4}>
        <Image src={src} alt={alt} w={320} h={180} fit="cover" radius="sm" />
      </HoverCard.Dropdown>
    </HoverCard>
  )
}

function ContentAccessCell({
  step,
  onChange,
  disabled,
  pending,
}: {
  step: StepWithValidation
  onChange: (step: StepWithValidation, access: ContentAccessType) => void
  disabled: boolean
  /** The access being saved, shown ahead of the server so the choice isn't ignored on screen. */
  pending?: ContentAccessType
}) {
  const facts = { type: step.contentType, showInAppSupported: step.showInAppSupported }
  const options = contentAccessOptions(facts)
  const value = pending ?? resolveContentAccess({ ...facts, showInApp: step.showInApp, useInRag: step.useInRag })

  // No source resource means the status snapshot hasn't landed or failed. Writing needs
  // its id, so the control would silently no-op — and with no flags to read the resolver
  // answers Full Access for a file nothing is known about yet.
  const unknown = !step.sourceResourceId

  // Shown rather than hidden when there is nothing to pick, so the row still says what
  // the file's access is and why it cannot be changed.
  const onlyOption = options.length === 1

  const select = (
    <Select
      size="xs"
      variant={onlyOption || unknown ? 'unstyled' : 'default'}
      disabled={disabled || onlyOption || unknown}
      allowDeselect={false}
      comboboxProps={{ withinPortal: true }}
      aria-label={`Content access for ${step.name || 'this file'}`}
      data={options.map(option => ({ value: option, label: CONTENT_ACCESS_LABELS[option] }))}
      value={unknown ? null : value}
      placeholder="—"
      onChange={next => next && onChange(step, next as ContentAccessType)}
    />
  )

  if (unknown) {
    return (
      <Tooltip label="Still reading this file's details." withArrow>
        <Box>{select}</Box>
      </Tooltip>
    )
  }

  // Held while the file processes, and while a change is saving. Both leave the control
  // greyed with its value still readable, so without this they read as a dead dropdown.
  if (disabled) {
    return (
      <Tooltip label={step.inProcess ? 'Available once this file finishes processing.' : 'Saving…'} withArrow>
        <Box>{select}</Box>
      </Tooltip>
    )
  }

  if (!onlyOption) return select

  return (
    <Tooltip label="The app has no viewer for this format, so it can only be used for answers." withArrow>
      <Box>{select}</Box>
    </Tooltip>
  )
}

const stepColDefs: TableColumnDef<StepWithValidation>[] = [
  {
    id: 'thumbnail',
    accessorKey: 'thumbnailUrl',
    header: () => <VisuallyHidden>Thumbnail</VisuallyHidden>,
    cell: props => (
      <ThumbnailCell
        src={props.row.original.thumbnailUrl}
        label={props.row.original.name || props.row.original.youtubeTitle}
      />
    ),
    size: 72,
    meta: { grow: false, label: 'Thumbnail' },
    enableGlobalFilter: false,
  },
  {
    id: 'name',
    // A YouTube file has no saved name until its first save; the video title stands in,
    // dimmed, since `valid` still keys off the saved name.
    accessorFn: row => row.name || row.youtubeTitle || '',
    header: 'File Name',
    cell: props => (
      <Text size="sm" fw={500} c={props.row.original.name ? undefined : 'dimmed'}>
        {props.getValue() ? <TableCellValue cell={props.cell} /> : 'Untitled'}
      </Text>
    ),
    size: 280,
  },
  {
    id: 'contentType',
    header: 'File Type',
    accessorFn: row => formatContentType(row.contentType),
    size: 100,
    meta: { grow: false },
  },
  // {
  //   accessorKey: 'title',
  //   header: 'Section Overview',
  //   Cell: props => <Text size="sm">{props.renderedCellValue}</Text>,
  //   grow: true,
  // },
  {
    id: 'status',
    header: 'File Status',
    size: 158,
    meta: { grow: false },
    // A badge per status and no ordering of its own — `getStepStatus` owns the ladder, so
    // this cannot disagree with the generation trigger reading the same function.
    cell: props => {
      const step = props.row.original

      switch (getStepStatus(step)) {
        case 'failed':
          return (
            <Tooltip
              label={getProcessErrorMessage(step.processProgressError, step.processProgressReason)}
              withArrow
              position="top"
              multiline
              maw={300}>
              <Badge color="red" variant="filled">
                Error
              </Badge>
            </Tooltip>
          )
        case 'analyzing':
          return <AnalyzingBadge progress={step.processProgress} />
        case 'preparing':
          return (
            <Badge color="yellow" variant="filled">
              Preparing
            </Badge>
          )
        case 'transcodeFailed':
          return (
            <Badge color="red" variant="filled">
              Transcode Error
            </Badge>
          )
        case 'unreviewed':
          return (
            <Badge color="orange" variant="filled">
              Review Needed
            </Badge>
          )
        case 'checking':
          return (
            <Tooltip label="Still loading this file's status" withArrow position="top">
              <Badge color="gray" variant="light">
                Checking…
              </Badge>
            </Tooltip>
          )
        default:
          return (
            <Badge color="green" variant="filled">
              Ready to Submit
            </Badge>
          )
      }
    },
  },
]
